import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Check, Trash2, Image as ImageIcon, Mic } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface FeedbackRow {
  id: string;
  user_id: string | null;
  feedback_type: string;
  message: string;
  page_url: string | null;
  device_info: any;
  screenshot_url: string | null;
  audio_url: string | null;
  status: string | null;
  created_at: string;
}

const typeLabel = (t: string) =>
  t === "bug"
    ? "Something broken"
    : t === "suggestion"
      ? "Idea"
      : t === "question"
        ? "Question"
        : "Note";

const statusLabel = (s: string | null) =>
  s === "resolved" ? "Resolved" : s === "in_progress" ? "In progress" : "New";

async function signedUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { data } = await supabase.storage
    .from("feedback-attachments")
    .createSignedUrl(path, 60 * 60);
  return data?.signedUrl ?? null;
}

export default function AdminFeedback() {
  const { toast } = useToast();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [urls, setUrls] = useState<Record<string, { screenshot?: string; audio?: string }>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const q = supabase
      .from("user_feedback")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    const { data, error } = await q;
    if (error) {
      toast({ title: "Couldn't load feedback", description: error.message, variant: "destructive" });
      setLoading(false);
      return;
    }
    const list = (data ?? []) as FeedbackRow[];
    setRows(list);

    // Mint short signed URLs for attachments we can display.
    const urlMap: Record<string, { screenshot?: string; audio?: string }> = {};
    await Promise.all(
      list.map(async (r) => {
        const [s, a] = await Promise.all([
          signedUrl(r.screenshot_url),
          signedUrl(r.audio_url),
        ]);
        urlMap[r.id] = { screenshot: s ?? undefined, audio: a ?? undefined };
      }),
    );
    setUrls(urlMap);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeAttachment = async (path: string | null) => {
    if (!path || /^https?:\/\//i.test(path)) return;
    await supabase.storage.from("feedback-attachments").remove([path]);
  };

  const markResolved = async (row: FeedbackRow) => {
    setBusyId(row.id);
    try {
      // Delete stored files so recordings and screenshots don't accumulate.
      await Promise.all([
        removeAttachment(row.screenshot_url),
        removeAttachment(row.audio_url),
      ]);
      const { error } = await supabase
        .from("user_feedback")
        .update({ status: "resolved", screenshot_url: null, audio_url: null })
        .eq("id", row.id);
      if (error) throw error;
      toast({ title: "Marked resolved", description: "Attachments removed from storage." });
      await load();
    } catch (e: any) {
      toast({ title: "Couldn't resolve", description: e?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const visible = rows.filter((r) =>
    showResolved ? true : (r.status ?? "new") !== "resolved",
  );

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Member Feedback</h1>
          <p className="text-sm text-muted-foreground">
            Notes members sent from the "Send feedback" button.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowResolved((v) => !v)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </Button>
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing here right now.</p>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => {
            const isResolved = (r.status ?? "new") === "resolved";
            const att = urls[r.id] || {};
            return (
              <Card key={r.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary">{typeLabel(r.feedback_type)}</Badge>
                      <Badge variant={isResolved ? "outline" : "default"}>{statusLabel(r.status)}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    {r.page_url && (
                      <a
                        href={r.page_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-muted-foreground underline break-all"
                      >
                        {r.page_url}
                      </a>
                    )}
                  </div>
                  {!isResolved && (
                    <Button
                      size="sm"
                      onClick={() => markResolved(r)}
                      disabled={busyId === r.id}
                    >
                      {busyId === r.id ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5 mr-1" />
                      )}
                      Mark resolved
                    </Button>
                  )}
                </div>

                <p className="text-sm whitespace-pre-wrap">{r.message}</p>

                {(att.screenshot || att.audio) && (
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    {att.screenshot && (
                      <a
                        href={att.screenshot}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1 underline text-muted-foreground"
                      >
                        <ImageIcon className="h-3.5 w-3.5" /> Screenshot
                      </a>
                    )}
                    {att.audio && (
                      <div className="flex items-center gap-2">
                        <Mic className="h-3.5 w-3.5 text-muted-foreground" />
                        <audio src={att.audio} controls className="h-7" />
                      </div>
                    )}
                    <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                      <Trash2 className="h-3 w-3" />
                      Files auto-delete on resolve
                    </span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
